import { Collection, Model } from 'backbone';
import _ from 'underscore';
import Analytics from '../analytics';
import Environment from '../environment';
import { ENGINES } from '../gameFactory.types';
import HwpTvApi from '../hwpTvApi';
import { getLogger } from '../logging/logger';
import fetchData from '../util/fetch';
import ImageLoader from '../util/imgLoader';
import { COMPARATORS, capitalize } from '../util/utilities';
import {
	ATTRIBUTES,
	BAR_TIMER_ORIENTATIONS,
	ENDSCREEN_COMPARATOR,
	ENDSCREEN_OPERANDS,
	EVENTS,
	HEADER_LABELS,
	ROUND_ATTRIBUTES,
	SCORE_TYPES,
} from './baseGame.types';
import BaseRound from './baseRound';
import GameClock from './gameClock';

/**
 * @typedef {import('../hwpTvApi').UserAnswerResponse} UserAnswerResponse
 * @typedef {import('../hwpTvApi').UserAnswer} UserAnswer
 * @typedef {import('./baseGame.types').BaseGameOptions} BaseGameOptions
 * @typedef {import('./baseGame.types').BaseRoundOptions} BaseRoundOptions
 * @typedef {import('./baseGame.types').EndscreenOptions} EndscreenOptions
 * @typedef {import('./leaderboards/leaderboards.types').LeaderboardOptions} LeaderboardOptions
 * @typedef {import('./leaderboards/leaderboards.types').LeaderboardConfig} LeaderboardConfig
 */

export {
	ATTRIBUTES,
	BAR_TIMER_ORIENTATIONS,
	ENDSCREEN_COMPARATOR,
	ENDSCREEN_OPERANDS,
	EVENTS,
	HEADER_LABELS,
	ROUND_ATTRIBUTES,
	SCORE_TYPES,
};

/**
 * @class
 * @template {BaseGameOptions} GameOptions
 * @template {BaseRoundOptions} RoundOptions
 * @template {BaseRound<RoundOptions>} RoundType
 * @extends Model<GameOptions>
 */
export default class BaseGame extends Model {
	get CN() {
		return BaseGame.name;
	}

	/**
	 * Returns the class to be used for rounds in this game.
	 * @returns {typeof RoundType}
	 */
	get RoundClass() {
		return BaseRound;
	}

	get logger() {
		return getLogger(this.CN);
	}

	/** @returns {Partial<GameOptions>} */
	defaults() {
		return {
			/** @type {ENGINES | undefined} The engine identifier */
			type: undefined,
			/** @type {string} Unique id for tracking game data and events against */
			gameId: '',
			/** @type {string} A URL of the initial game data JSON file */
			contentUrl: '',
			/** @type {string[]|undefined} Array of round ids that should be used for this game instance */
			content: undefined,
			/** @type {boolean} Flag to indicate if rounds should be shuffled each time */
			shuffle: false,
			/** @type {number} Allows limiting the number of rounds for a game */
			contentLimit: Infinity,
			/** @type {boolean} Flag to indicate if any persisted round data for the user/game should be loaded from the server */
			fetchProgress: true,
			/** @type {boolean} Allows rounds to be repeated during gameplay. Prioritizes the rounds that have been answered the least amount of times */
			repeatQuestions: false,
			/**
			 * @type {number | undefined} The time limit for the game clock.
			 * Undefined == no timing functionality.
			 * Infinity == clock counts to measure time elapsed.
			 * Finite number == clock counds down from timeLimit.
			 * */
			timeLimit: undefined,
			/** @type {number} The time during countdown when the clock should indicate time is running out. */
			warningTime: 0,
			/** @type {'horizontal'|'vertical'|undefined} Visual bar timer orientation. */
			timerOrientation: undefined,
			/** @type {number} Used for count down visual. Provide a number to count down from to 0. Providing 0 will skip the countdown */
			countDown: 0,
			/**
			 * @type {string[]|undefined} Use for a visual count down but will show text instead of a 3, 2, 1 style countdown.
			 * @example ['Ready', 'Set', 'Go!']
			 * */
			countValues: undefined,
			/** @type {SCORE_TYPES} Indicates if the score should be points based or time based */
			scoreType: SCORE_TYPES.points,
			/** @type {number} The number of points awarded to user per round or question. May be used differently across engines. */
			pointsPerQuestion: 1,
			/** @type {object[]} Array of objects representing pages from the arcade to be used. Typically for endscreen buttons, but sometimes used in other ways. */
			pages: [],
			/** @type {object[]} Array of objects representing endscreen configurations that can be used. */
			endscreen: [],

			/** @type {string} The primary text to be displayed in the game header */
			title: '',
			/** @type {string} The secondary text to be displayed in the game header */
			subtitle: '',
			/** @type {string} The name to be displayed for the game in places like the leaderboard tab names */
			name: '',

			/** @type {Record<HEADER_LABELS, string>} Object to hold various labels used by the view in the engine. Allows customizing labels through config data. */
			labels: {
				timer: 'TIME',
				progress: 'ROUND',
			},

			/* RUNTIME VALUES */
			/** @type {number} Tracks the current round of the game the user is on */
			roundIndex: -1,
			/** @type {number} The current score for the user. **Not used by most engines** */
			score: 0,
			/** @type {import('./leaderboards/leaderboards.types').LeaderboardConfig} Configuration data for a games leaderboards */
			leaderboard: {
				sort: 'desc',
				template: 'default',
				useNodeLeaderboards: false,
				// pages: [], // TODO: Is this ever used?
			},
		};
	}

	/** Convenience getter for accessing the 'type' attribute */
	get type() {
		return this.get(ATTRIBUTES.type);
	}

	url() {
		return this.get(ATTRIBUTES.contentUrl);
	}

	/**
	 * Convience method to automatically add the 'engine' and 'gameId' properties for
	 * GA4 analytics calls.
	 * @param {string} eventName Name of the event to send to GA
	 * @param {object} [eventData] Additional data of event to send to GA
	 */
	sendGA4Event(eventName, eventData) {
		this.logger.log(`[${this.sendGA4Event.name}]`, { eventName, eventData });

		const { type, gameId } = this.attributes;
		return Analytics.sendEvent(eventName, {
			engine: capitalize(type),
			gameId,
			...eventData,
		});
	}

	/**
	 * Constructs a new instance of the BaseGame class.
	 * @param {GameOptions} attributes - The initial attributes for the game.
	 * @param {object} [options] - Additional options for the game.
	 * @constructor
	 */
	constructor(attributes, options) {
		super(attributes, options);

		/** @type {Collection<RoundType>} */
		this.rounds = new Collection([], { model: this.RoundClass });
	}

	/**
	 * @param {GameOptions} gameData Data needed to populate the game. At least has a contentUrl,
	 * at most has all required data.
	 * @returns {Promise<this>}
	 */
	initialize(gameData) {
		this.logger.log(`[${this.initialize.name}]`, { gameData });

		return fetchData(this.url())
			.then((response) => this.parse?.(response) || response)
			.then(
				(response) => {
					this.logger.debug({ response });
					/**
					 * We have an inconsistency in our naming between content sheets. We handle
					 * both cases of a 'data' or 'rounds' array.
					 * */
					const { data, rounds, ...attributes } = response;
					this.set(attributes);

					// Configure game clock
					if (this.get('timeLimit')) {
						this.clock = new GameClock(
							this.pick('timeLimit', 'tickInterval', 'warningTime'),
						);
					}

					const { content, shuffle, contentLimit } = this.attributes;
					/** @type {{id: string}[]} */
					let roundData = data || rounds;
					this.logger.debug({ content, shuffle, contentLimit, roundData });
					if (content) {
						// Limit content
						roundData = roundData.filter(({ id }) => content.includes(id));
					}
					if (shuffle) {
						// Shuffle content
						roundData = _.shuffle(roundData);
					}
					if (contentLimit) {
						// Cut content down to limit
						roundData.splice(contentLimit);
					}

					this.rounds.set(roundData);
					return this.fetchGameProgress();
				},
				(/** @type {Error} */ error) => {
					this.logger.error('[failed to fetch content]', error);
					return Promise.reject(error);
				},
			)
			.finally(() => this.onInitialized());
	}

	/**
	 * @override
	 * @param {any} response
	 * @param {any} [options]
	 *
	 * @returns {any}
	 */
	parse(response, options) {
		this.logger.log(`[${this.parse.name}]`, { response, options });

		const convertInfinityTimeLimit = (/** @type {{timeLimit: string | number}} */ obj) => {
			const timeLimit = obj[ATTRIBUTES.timeLimit];
			if (
				timeLimit &&
				typeof timeLimit === 'string' &&
				new RegExp(Infinity.toString(), 'i').test(timeLimit)
			) {
				obj[ATTRIBUTES.timeLimit] = Infinity;
			}
		};

		// Convert game wide timeLimit value
		convertInfinityTimeLimit(response);

		// Extract rounds or deprecated 'data' array to convert round specific timeLimits.
		const { rounds = [], data = [] } = response;
		[...rounds, ...data].forEach(convertInfinityTimeLimit);

		return response;
	}

	/**
	 * This function is called after all data has been set.
	 * Override in child classes to extend initialization functionality.
	 *
	 * @returns {Promise<this>}
	 */
	onInitialized() {
		this.logger.log(`[${this.onInitialized.name}]`);

		this.sendGA4Event('GameInitialized', {});
		this.trigger(EVENTS.initialized, this);

		return Promise.resolve(this);
	}

	/**
	 * Traverses an object or array and loads all images detected
	 *
	 * @param {object|any[]} [target]
	 * @returns {Promise<any>}
	 */
	preloadContent(target) {
		target = target || this.rounds.toJSON();

		const isImageRegex = /\.(jpg|jpeg|png|gif)/;
		const content = {};
		const processObject = (obj) => {
			_.values(obj).forEach((value) => {
				const isImage = isImageRegex.test(value);
				if (_.isString(value) && isImage) {
					content[value] = content[value] + 1 || 1;
				} else if (typeof value === 'object' && value != null) {
					processObject(value);
				}
			});
		};
		processObject(target);
		return ImageLoader.loadImages(Object.keys(content));
	}

	fetchGameProgress() {
		this.logger.log(`[${this.fetchGameProgress.name}]`);

		const { fetchProgress, gameId } = this.attributes;

		if (fetchProgress && gameId) {
			return HwpTvApi.getGameUserAnswer(gameId).then(
				(response) => {
					const { data } = /** @type {UserAnswerResponse} */ (response);
					this.syncAnswers(data);
					return data;
				},
				(/** @type {Error} */ error) => {
					this.logger.error('[Error fetching game progress]', { error });
					return Promise.reject(error);
				},
			);
		}

		this.logger.warn('[Skipping fetch progress]');
		return Promise.resolve();
	}

	/**
	 * Pulls previous user answers from API and applys them to the current game.
	 *
	 * @param {UserAnswer[]} answersArray Array of answers
	 */
	syncAnswers(answersArray) {
		this.logger.log(`[${this.syncAnswers.name}]`, { answersArray, rounds: this.rounds });

		if (!this.rounds.length) {
			return;
		}

		answersArray.forEach(({ answer, questionId, score, timesAnswered = 0 }) => {
			if (questionId && this.rounds.has(questionId)) {
				this.rounds
					.get(questionId)
					.set('selectedOptionId', answer)
					.set('timesAnswered', timesAnswered)
					.set('score', score);
			}
		});

		const lastAnsweredIndex = this.rounds.findLastIndex((round) =>
			round.has('selectedOptionId'),
		);
		const lastRoundIndex = this.rounds.length - 1;

		if (lastAnsweredIndex < lastRoundIndex) {
			/**
			 * Only update our round index if the user has not answered all questions. If they have
			 * answered all questions just start them back at 0.
			 */
			this.roundIndex(lastAnsweredIndex);
		}
	}

	/**
	 * Convience method for finding a round specific attribtue value or defaulting to
	 * getting the value from the main Game object.
	 *
	 * @param {Backbone._StringKey<GameOptions> | Backbone._StringKey<RoundOptions>} attributeName The name of the ATTRIBUTE
	 * @param {RoundType} [round] The round model to pull the attribute from.
	 * @returns {any} The attribute value
	 */
	getRoundOrGameAttribute(attributeName, round = this.getCurrentRound()) {
		this.logger.log(`[${this.getRoundOrGameAttribute.name}]`, { attributeName, round });

		const roundAttributes = round?.attributes || {};
		const gameAttributes = this.attributes;

		const value = roundAttributes[attributeName] ?? gameAttributes[attributeName];
		this.logger.debug({ attributeName, value });

		return value;
	}

	prevRound() {
		this.logger.log(`[${this.prevRound.name}]`);

		const roundIndex = this.roundIndex(Math.max(0, this.roundIndex() - 1));
		const currentRound = this.getCurrentRound();
		if (currentRound) {
			this.trigger(EVENTS.gameRound, currentRound, roundIndex);
			this.sendGA4Event('GameRound', { roundIndex, roundId: currentRound.id });
		}
		return currentRound;
	}

	nextRound() {
		this.logger.log(`[${this.nextRound.name}]`);

		let roundIndex = this.roundIndex(this.roundIndex() + 1);

		if (this.get('repeatQuestions')) {
			// Sorts by 'timesAnswered' then 'id' to serve up the first least seen question.
			this.rounds.reset(
				this.rounds
					.chain()
					.sortBy(ROUND_ATTRIBUTES.id) // On model itself, no need to extract attribute
					.sortBy((/** @type {RoundType} */ round) => round.get('timesAnswered') || 0)
					.value(),
			);
			roundIndex = this.roundIndex(0);
		}

		const currentRound = this.getCurrentRound();

		const { gameId = '', type = this.type } = this.attributes;

		if (currentRound) {
			if (this.clock) {
				this.clock.timeLimit = this.getRoundOrGameAttribute('timeLimit', currentRound);
			}
			const { rewards } = currentRound.attributes;
			this.trigger(EVENTS.gameRound, currentRound, roundIndex);
			this.sendGA4Event('GameRound', { roundId: currentRound.id, rewards });
		} else {
			const score = this.getScore();
			let possibleScore;
			if (Environment.isHub() || Environment.isInHub()) {
				// Only send possible score if we're in a hub experience to trigger XP calculation
				possibleScore = this.getPossibleScore();
			}
			HwpTvApi.addGameScore(gameId, score, type, possibleScore);
			this.trigger(EVENTS.gameComplete);

			const { rewards = [] } = /** @type {EndscreenOptions} */ (this.getEndscreenData());
			this.sendGA4Event('GameComplete', { rewards });
		}
		return currentRound;
	}

	/**
	 * Gets the current round if it exists.
	 * @returns {RoundType | undefined} The current round
	 */
	getCurrentRound() {
		this.logger.log(`[${this.getCurrentRound.name}]`, this.rounds.length);

		const roundIndex = this.roundIndex();
		const numRounds = this.rounds.length || 0;

		if (roundIndex < -1) {
			this.roundIndex(-1);
			return undefined;
		}

		if (roundIndex >= numRounds) {
			this.roundIndex(numRounds);
			return undefined;
		}

		return this.rounds.at(roundIndex);
	}

	/**
	 * Gets points for the specificed round and defaults to the currect round. Sets points if the value is provided.
	 * @param {Number} [points] Number of points to set on the given round
	 * @param {Number} [roundIndex=this.roundIndex()] The index of the round to operate on.
	 * @returns {Number} Points for the current or specifed round
	 */
	roundPoints(points, roundIndex = this.roundIndex()) {
		this.logger.log(`[${this.roundPoints.name}]`, { points, roundIndex });

		if (roundIndex < 0 || roundIndex > this.rounds.length) {
			this.logger.error('Invalid round index passed into roundPoints');
			return 0;
		}

		// Get round data and points
		const round = this.rounds.at(roundIndex);

		// Set points for round if supplied. Use 'selectedOptionid' property because Legacy :(
		if (round && points) {
			round.set(ROUND_ATTRIBUTES.selectedOptionId, points);
		}

		return round.get(ROUND_ATTRIBUTES.selectedOptionId) || 0;
	}

	addRoundPoints(points = 0) {
		this.logger.log(`[${this.addRoundPoints.name}]`, { points });

		if (!Number.isFinite(points)) {
			this.logger.error('non-finite value passed into addRoundPoints');
			return this.roundPoints();
		}

		// grab our existing score, add points, recall to set current round's points
		const currentPoints = this.roundPoints();
		return this.roundPoints(currentPoints + points);
	}

	pointsPerQuestion() {
		this.logger.log(`[${this.pointsPerQuestion.name}]`);

		return this.getRoundOrGameAttribute('pointsPerQuestion');
	}

	/**
	 * Calculates the current amount of points earned by a player in a given round, based on
	 * the amount time left on the GameClock iff the ScoreType is timed and there's a GameClock
	 * present with a timeLimit. Otherwise, it will simply return pointsPerQuestion or 0.
	 *
	 * @param {RoundType} [round=this.getCurrentRound()] The round in question. Defaults to current round.
	 * @returns {number} Points awarded for the round (in points or milliseconds depending on scoreType)
	 */
	getRoundScore(round = this.getCurrentRound()) {
		this.logger.debug(`[${this.getRoundScore.name}]`, { round });

		// Handle case where no valid round is provided
		if (!round) {
			this.logger.warn('No valid round provided to getRoundPoints');
			return 0;
		}

		// Cache attribute values to avoid redundant calls
		const pointsPerQuestion = this.getRoundOrGameAttribute('pointsPerQuestion', round) || 1;
		const scoreType = this.getRoundOrGameAttribute('scoreType', round);
		this.logger.debug({ scoreType, pointsPerQuestion, round });

		// Early return for non-time-based scoring
		if (scoreType === SCORE_TYPES.points) {
			return pointsPerQuestion;
		}

		// Handle time-based scoring
		if (!this.clock) {
			this.logger.warn('Time-based scoring requested but no clock is available');
			return pointsPerQuestion;
		}

		const timeLimit = this.clock.getTimeLimit();
		const timeRemaining = this.clock.getRemainingTime() || 0;
		const timeElapsed = this.clock.getElapsedTime() || 0;
		const hasTimeLimit = timeLimit && timeLimit !== Infinity;
		this.logger.debug({ timeLimit, timeRemaining, timeElapsed, hasTimeLimit });

		// Calculate points based on score type
		if (scoreType === SCORE_TYPES.timed) {
			// Score is calculated purely based on the game clock
			if (hasTimeLimit && timeRemaining) {
				return timeRemaining;
			}

			if (!hasTimeLimit && timeElapsed) {
				return timeElapsed;
			}

			this.logger.warn('Unable to calculate points possible for timed score type');
			return 0;
		}

		if (scoreType === SCORE_TYPES.timedPoints && hasTimeLimit) {
			// Protect against division by zero
			if (timeLimit <= 0) {
				this.logger.warn('Time limit is zero or negative, cannot calculate progress');
				return 0;
			}

			const progress = timeRemaining / timeLimit;
			return progress * pointsPerQuestion;
		}

		// If we reach here, it's either an unrecognized score type or a case not handled above
		// Default to standard points scoring behavior
		if (scoreType !== SCORE_TYPES.points) {
			this.logger.warn(
				`Unrecognized or unsupported score type: ${scoreType}, defaulting to standard points scoring`,
			);
		}

		return pointsPerQuestion;
	}

	/**
	 * Gets the total score for the game by summing the scores for each round.
	 *
	 * @returns {number} The total score for the game.
	 */
	getScore() {
		this.logger.log(`[${this.getScore.name}]`);

		if (!this.rounds) {
			return 0;
		}

		return this.rounds.reduce((sum, round) => {
			const {
				[ROUND_ATTRIBUTES.selectedOptionId]: selectedOptionId,
				[ROUND_ATTRIBUTES.score]: score,
			} = round.attributes;

			if (typeof score === 'number' && Number.isFinite(score)) {
				return sum + score;
			}

			if (typeof selectedOptionId === 'number' && Number.isFinite(selectedOptionId)) {
				return sum + selectedOptionId;
			}

			return sum;
		}, 0);
	}

	getPossibleScore() {
		this.logger.log(`[${this.getPossibleScore.name}]`);

		const { pointsPerQuestion = 0 } = this.attributes;
		const { rounds = [] } = this;

		return rounds.length * pointsPerQuestion;
	}

	/**
	 * Searches through endscreen array for the first endscreen with a passing
	 * condition. If no endscreens match conditions, the first available one will
	 * be returned.
	 * @returns {EndscreenOptions | undefined} The endscreen ID or undefined if none match.
	 */
	getEndscreenData() {
		this.logger.log(`[${this.getEndscreenData.name}]`);

		const { endscreen: endscreens = [] } = this.attributes;
		if (!endscreens.length) {
			return undefined;
		}

		const replacementValues = this.getEndscreenReplacementValues();

		const endscreenData = endscreens.find((data) => {
			const { condition } = data;
			if (!condition) {
				return false;
			}
			const { propertyName, comparator, targetValue } = condition;
			const compareFunction = COMPARATORS[comparator];
			const compareValue = replacementValues[propertyName];

			return compareFunction(compareValue, targetValue);
		});

		// Default to the first available endscreen if none was found.
		return endscreenData || endscreens[0];
	}

	/**
	 * Gets the replacement values for the endscreen data.
	 * @returns {Object} An object containing the score and percentage values for the endscreen.
	 */
	getEndscreenReplacementValues() {
		this.logger.log(`[${this.getEndscreenReplacementValues.name}]`);

		const score = this.getScore();
		const possibleScore = this.getPossibleScore();

		return {
			[ENDSCREEN_OPERANDS.score]: score,
			[ENDSCREEN_OPERANDS.percentage]: score / possibleScore,
		};
	}

	resetGame() {
		this.logger.log(`[${this.resetGame.name}]`);

		this.set(ATTRIBUTES.score, 0);
		this.roundIndex(-1);
		if (this.clock) {
			this.clock.reset();
		}
	}

	/**
	 * Gets the current round index. Optionally sets the current round index if provided.
	 *
	 * @param {number} [newRoundIndex] The round to set the game to
	 * @returns {number}
	 */
	roundIndex(newRoundIndex) {
		this.logger.log(`[${this.roundIndex.name}]`);

		if (Number.isFinite(newRoundIndex)) {
			this.set(ATTRIBUTES.roundIndex, newRoundIndex);
		}

		return this.get(ATTRIBUTES.roundIndex);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		this.off();

		this.clock?.stop?.();
		this.clock?.off?.();
		this.clock = null;

		this.rounds.each((/** @type {Model} */ round) => round?.off?.());
		this.rounds.clear?.();
		this.rounds.off?.();
	}
}
